// src/components/Periods/PeriodManager.js - Updated for collaborator accounts
import React, { useState, useEffect } from "react";
import { auth, db, storage } from "../../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  addDoc,
  setDoc,
  getDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { parseOFXFile } from "../../utils/OFXParser";
import MainLayout from "../Layout/MainLayout";
import { useUserContext } from "../../contexts/UserContext"; // Import the user context
import "./PeriodManager.css";


const PeriodManager = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Get user subscription and account information from context
  const { 
    user: contextUser, 
    subscription, 
    hasAccess,
    activeAccountType,  // Tipo de conta ativa
    activeBusinessProfileId, // ID do perfil de negócio ativo - IMPORTANTE para múltiplos perfis
    getAccountSubpath,   // Função auxiliar para obter o subcaminho
    businessProfiles,  // Perfis empresariais disponíveis
    isCollaborator,    // NOVO: Indica se o usuário é colaborador
    checkProfileAccess // NOVO: Verifica o nível de acesso do usuário
  } = useUserContext();

  // NOVO: Estado para armazenar informações do proprietário do workspace (para colaboradores)
  const [workspaceOwnerInfo, setWorkspaceOwnerInfo] = useState(null);
  
  // Estados para o formulário de novo período
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());

  // Criar array de meses para o select
  const months = [
    { value: "01", label: "Janeiro" },
    { value: "02", label: "Fevereiro" },
    { value: "03", label: "Março" },
    { value: "04", label: "Abril" },
    { value: "05", label: "Maio" },
    { value: "06", label: "Junho" },
    { value: "07", label: "Julho" },
    { value: "08", label: "Agosto" },
    { value: "09", label: "Setembro" },
    { value: "10", label: "Outubro" },
    { value: "11", label: "Novembro" },
    { value: "12", label: "Dezembro" }
  ];

  // Get current date information
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-based month (January is 1)

  // NOVO: Verifica se o colaborador tem permissões para manipular períodos
  const [canManagePeriods, setCanManagePeriods] = useState(true);

  // NOVO: Efeito para verificar permissões de colaborador
  useEffect(() => {
    const checkCollaboratorPermissions = async () => {
      if (isCollaborator && activeBusinessProfileId) {
        // Verifica permissões para o perfil ativo
        const { isOwner } = checkProfileAccess(activeBusinessProfileId);
        setCanManagePeriods(isOwner);
        
        // Obter informações do proprietário do workspace para colaboradores
        try {
          const userDoc = await getDoc(doc(db, 'users', contextUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const invitedBy = userData.invitedBy;
            
            if (invitedBy) {
              const ownerDoc = await getDoc(doc(db, 'users', invitedBy));
              if (ownerDoc.exists()) {
                setWorkspaceOwnerInfo({
                  uid: invitedBy,
                  name: ownerDoc.data().name || ownerDoc.data().displayName || 'Proprietário do Workspace'
                });
              }
            }
          }
        } catch (error) {
          console.error("Erro ao obter informações do proprietário:", error);
        }
      } else {
        // Usuários normais têm permissão total
        setCanManagePeriods(true);
      }
    };

    checkCollaboratorPermissions();
  }, [isCollaborator, activeBusinessProfileId, contextUser, checkProfileAccess]);

  // Verificação direta do plano do usuário através dos dados do Firebase
  // Isso deverá ser mais preciso do que a função hasAccess
  const getUserPlanType = () => {
    // Se for colaborador, usar o plano do proprietário do workspace
    if (isCollaborator) {
      return "business"; // Assume que o proprietário tem pelo menos o plano business
    }
    
    // Verificar no objeto do usuário do contexto
    if (contextUser?.planType) {
      return contextUser.planType;
    }

    // Verificar no objeto de assinatura
    if (subscription?.planType) {
      return subscription.planType;
    }

    // Verificar diretamente no Firestore - carregado no estado user
    if (user?.planType) {
      return user.planType;
    }

    // Se não encontrar, assumir plano básico
    return "basic";
  };

  const userPlanType = getUserPlanType();

  // Verificar explicitamente o tipo de plano
  const hasPro = userPlanType === "business_pro";
  const hasBusiness = userPlanType === "business" || userPlanType === "business_pro";

  // Calculate how far back the user can go based on their plan
  const getYearsArray = () => {
    // Business Pro users can go back 2 years
    if (hasPro) {
      return [currentYear, currentYear - 1, currentYear - 2];
    }
    // Business users can only go back 3 months (which might span to previous year)
    else if (hasBusiness && activeAccountType === 'business') {
      // Check if 3 months ago was in the previous year
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(currentDate.getMonth() - 3);

      if (threeMonthsAgo.getFullYear() < currentYear) {
        return [currentYear, currentYear - 1];
      } else {
        return [currentYear];
      }
    }
    // Personal plan: allow current year and previous year if 12 months back spans to it
    else if (userPlanType === 'personal' || activeAccountType === 'personal') {
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(currentDate.getMonth() - 12);

      if (twelveMonthsAgo.getFullYear() < currentYear) {
        return [currentYear, currentYear - 1];
      } else {
        return [currentYear];
      }
    }
    // Default/Basic: only current year
    else {
      return [currentYear];
    }
  };

  // Get available months for selected year
  const getAvailableMonths = () => {
    // Calculate the 3-month limit date for business users
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(currentDate.getMonth() - 3);

    // Calculate the 12-month limit date for personal users
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(currentDate.getMonth() - 12);

    // Current month as number
    const currentMonthNum = currentDate.getMonth() + 1;

    // If current year, only show months up to current month
    if (parseInt(year) === currentYear) {
      // For business users, limit to last 3 months
      if (hasBusiness && !hasPro && activeAccountType === 'business') {
        const threeMonthsAgoNum = threeMonthsAgo.getMonth() + 1;
        return months.filter(m => {
          const monthNum = parseInt(m.value);
          return monthNum <= currentMonthNum && monthNum >= threeMonthsAgoNum;
        });
      }
      // For personal users, limit to last 12 months in current year
      else if (userPlanType === 'personal' || activeAccountType === 'personal') {
        // If 12 months ago is in the previous year, all current year months are allowed
        if (twelveMonthsAgo.getFullYear() < currentYear) {
          return months.filter(m => parseInt(m.value) <= currentMonthNum);
        } else {
          // If 12 months ago is in the current year, limit from that month
          const twelveMonthsAgoNum = twelveMonthsAgo.getMonth() + 1;
          return months.filter(m => {
            const monthNum = parseInt(m.value);
            return monthNum <= currentMonthNum && monthNum >= twelveMonthsAgoNum;
          });
        }
      }
      else {
        // Pro users or basic users in current year just limit to current month
        return months.filter(m => parseInt(m.value) <= currentMonthNum);
      }
    }

    // If previous year
    if (parseInt(year) === currentYear - 1) {
      // Business users (not pro) can only see months from 3 months ago
      if (hasBusiness && !hasPro && activeAccountType === 'business') {
        // If 3 months ago is in the previous year
        if (threeMonthsAgo.getFullYear() === currentYear - 1) {
          const fromMonth = threeMonthsAgo.getMonth() + 1;
          return months.filter(m => parseInt(m.value) >= fromMonth);
        } else {
          return []; // No months available from previous year
        }
      }
      // Pro users can see all months from previous year
      else if (hasPro) {
        return months;
      }
      // Personal plan: show months from 12 months ago
      else if (userPlanType === 'personal' || activeAccountType === 'personal') {
        if (twelveMonthsAgo.getFullYear() === currentYear - 1) {
          const fromMonth = twelveMonthsAgo.getMonth() + 1;
          return months.filter(m => parseInt(m.value) >= fromMonth);
        } else {
          return []; // No months available from previous year
        }
      }
    }

    // For 2 years ago (only pro users)
    if (parseInt(year) === currentYear - 2) {
      if (hasPro) {
        return months;
      } else {
        return []; // No months available if not pro
      }
    }

    return [];
  };

  // Keep years and available months updated
  const [availableYears, setAvailableYears] = useState(getYearsArray());
  const [availableMonths, setAvailableMonths] = useState([]);

  // Update available years and months when component mounts or subscription changes
  useEffect(() => {
    const years = getYearsArray();
    setAvailableYears(years);

    // If current year is not in the available years, reset to first available
    if (!years.includes(parseInt(year))) {
      setYear(years[0] || currentYear);
    }
  }, [hasAccess, hasPro, hasBusiness, activeAccountType]); // Adicionado activeAccountType como dependência

  // Update available months when year changes or on mount
  useEffect(() => {
    const months = getAvailableMonths();
    setAvailableMonths(months);

    // Reset month selection if current selection is no longer valid
    if (month && !months.some(m => m.value === month)) {
      setMonth("");
    }
  }, [year, hasPro, hasBusiness, userPlanType, activeAccountType]); // Adicionado activeAccountType como dependência


  // Carregar usuário e períodos existentes
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        if (auth.currentUser) {
          setUser(auth.currentUser);
          await loadPeriods();
        }
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        setError("Não foi possível carregar os dados iniciais.");
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [activeAccountType, activeBusinessProfileId]); // Adicionar activeBusinessProfileId como dependência

  // Carregar períodos existentes - MODIFICADO para suportar colaboradores
  const loadPeriods = async () => {
    try {
      if (!auth.currentUser) return;

      // Obter informações do caminho da conta, incluindo userId e subpath
      // Se o usuário for colaborador, getAccountPath retornará o ID do proprietário
      const { userId, subpath } = await getAccountPath();
      
      // Exibir o subcaminho para debug (opcional)
      console.log(`Carregando períodos de: ofxFiles/${userId}/${subpath}`);
      
      const periodsQuery = query(
        collection(db, "ofxFiles", userId, subpath),
        orderBy("period", "desc")
      );

      const periodsSnapshot = await getDocs(periodsQuery);
      const periodsData = [];

      periodsSnapshot.forEach(doc => {
        const data = doc.data();
        periodsData.push({
          id: doc.id,
          fileName: data.fileName || "Arquivo sem nome",
          uploadDate: data.uploadDate?.toDate() || new Date(),
          period: data.period || "",
          periodLabel: data.periodLabel || "",
          month: data.month || "",
          year: data.year || "",
          filePath: data.filePath || null,
          transactionCount: data.transactionCount || 0,
          bankInfo: data.bankInfo || { org: "Desconhecido" },
          ownerUid: userId // NOVO: Armazenar ID do proprietário
        });
      });

      setPeriods(periodsData);
    } catch (error) {
      console.error("Erro ao carregar períodos:", error);
      setError("Não foi possível carregar os períodos.");
    }
  };

  // NOVO: Função auxiliar para obter o caminho completo da conta
  const getAccountPath = async () => {
    try {
      // MODIFICADO para suportar colaboradores
      if (isCollaborator && activeBusinessProfileId) {
        // Para colaboradores, verificamos o documento do usuário para obter o ID do proprietário
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const invitedBy = userData.invitedBy;
          
          if (invitedBy) {
            return {
              userId: invitedBy, // ID do proprietário
              subpath: activeBusinessProfileId,
              isCollaborator: true
            };
          }
        }
      }
      
      // Para usuários normais, usar o próprio ID e o subcaminho correto
      return {
        userId: auth.currentUser.uid,
        subpath: getAccountSubpath(),
        isCollaborator: false
      };
    } catch (error) {
      console.error('Erro ao obter caminho da conta:', error);
      
      // Fallback para 'business' em caso de erro
      return {
        userId: auth.currentUser.uid,
        subpath: 'business',
        isCollaborator: false
      };
    }
  };

  // Manipular mudança de arquivo
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.name.endsWith('.ofx') || selectedFile.name.endsWith('.OFX'))) {
      setFile(selectedFile);

      try {
        // Preview do arquivo para extrair informações do banco
        const result = await parseOFXFile(selectedFile);

        // Tentar detectar mês/ano do nome do arquivo, por ex: extrato_202312.ofx
        const fileNameMatch = selectedFile.name.match(/(\d{4})(\d{2})/);
        if (fileNameMatch) {
          const [, yearStr, monthStr] = fileNameMatch;
          if (monthStr >= '01' && monthStr <= '12') {
            // Verify that this month is allowed based on subscription
            const extractedYear = parseInt(yearStr);
            const extractedMonth = monthStr;

            const selectedDate = new Date(extractedYear, parseInt(extractedMonth) - 1, 1);
            const now = new Date();

            // Don't set future dates
            if (selectedDate > now) {
              return;
            }

            // Check if the date is allowed based on subscription
            let isDateAllowed = false;

            if (hasAccess("business_pro")) {
              // Business Pro: up to 2 years back
              const twoYearsAgo = new Date();
              twoYearsAgo.setFullYear(now.getFullYear() - 2);
              twoYearsAgo.setDate(1); // Normalize to the start of the month
              twoYearsAgo.setHours(0,0,0,0);
              isDateAllowed = selectedDate >= twoYearsAgo;
            } else if (hasAccess("business") && activeAccountType === 'business') {
              // Business: up to 3 months back
              const threeMonthsAgo = new Date();
              threeMonthsAgo.setMonth(now.getMonth() - 3);
              threeMonthsAgo.setDate(1); // Normalize to the start of the month
              threeMonthsAgo.setHours(0,0,0,0);
              isDateAllowed = selectedDate >= threeMonthsAgo;
            } else if (userPlanType === 'personal' || activeAccountType === 'personal') {
              // Personal: up to 12 months back
              const twelveMonthsAgo = new Date();
              twelveMonthsAgo.setMonth(now.getMonth() - 12);
              twelveMonthsAgo.setDate(1); // Normalize to the start of the month
              twelveMonthsAgo.setHours(0,0,0,0);
              isDateAllowed = selectedDate >= twelveMonthsAgo;
            } else {
              // Default: current month only
              const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
              firstDayOfCurrentMonth.setHours(0,0,0,0);
              isDateAllowed = selectedDate >= firstDayOfCurrentMonth;
            }

            if (isDateAllowed && availableYears.includes(extractedYear)) {
              setYear(extractedYear);
              // Only set month if it's available for the selected year
              // Need to call getAvailableMonths with the potentially new year
              const monthsForExtractedYear = getAvailableMonthsForYear(extractedYear);
              if (monthsForExtractedYear.some(m => m.value === extractedMonth)) {
                setMonth(extractedMonth);
              }
            }
          }
        }
      } catch (error) {
        console.error("Erro ao analisar o arquivo:", error);
        // Ainda permitimos o upload mesmo que haja erro na pré-análise
      }
    } else {
      setFile(null);
      setError("Por favor, selecione um arquivo OFX válido.");
    }
  };

  // Helper function to get available months for a specific year (used in handleFileChange)
  const getAvailableMonthsForYear = (specificYear) => {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(currentDate.getMonth() - 3);
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(currentDate.getMonth() - 12);
    const currentMonthNum = currentDate.getMonth() + 1;

    if (parseInt(specificYear) === currentYear) {
      if (hasBusiness && !hasPro && activeAccountType === 'business') {
        const threeMonthsAgoNum = threeMonthsAgo.getMonth() + 1;
        return months.filter(m => parseInt(m.value) <= currentMonthNum && parseInt(m.value) >= threeMonthsAgoNum);
      } else if (userPlanType === 'personal' || activeAccountType === 'personal') {
        if (twelveMonthsAgo.getFullYear() < currentYear) {
          return months.filter(m => parseInt(m.value) <= currentMonthNum);
        } else {
          const twelveMonthsAgoNum = twelveMonthsAgo.getMonth() + 1;
          return months.filter(m => parseInt(m.value) <= currentMonthNum && parseInt(m.value) >= twelveMonthsAgoNum);
        }
      } else {
        return months.filter(m => parseInt(m.value) <= currentMonthNum);
      }
    }
    if (parseInt(specificYear) === currentYear - 1) {
      if (hasBusiness && !hasPro && activeAccountType === 'business') {
        if (threeMonthsAgo.getFullYear() === currentYear - 1) {
          const fromMonth = threeMonthsAgo.getMonth() + 1;
          return months.filter(m => parseInt(m.value) >= fromMonth);
        } else { return []; }
      } else if (hasPro) { return months; }
      else if (userPlanType === 'personal' || activeAccountType === 'personal') {
        if (twelveMonthsAgo.getFullYear() === currentYear - 1) {
          const fromMonth = twelveMonthsAgo.getMonth() + 1;
          return months.filter(m => parseInt(m.value) >= fromMonth);
        } else { return []; }
      }
    }
    if (parseInt(specificYear) === currentYear - 2 && hasPro) { return months; }
    return [];
  };


  // Adicionar novo período - MODIFICADO para suportar colaboradores
  const handleAddPeriod = async () => {
    if (!file) {
      setError("Por favor, selecione um arquivo OFX.");
      return;
    }

    if (!month) {
      setError("Por favor, selecione o mês do período.");
      return;
    }

    // NOVO: Verificar se colaborador tem permissão para adicionar períodos
    if (isCollaborator && !canManagePeriods) {
      setError("Você não tem permissão para adicionar períodos neste workspace. Entre em contato com o proprietário do workspace.");
      return;
    }

    // Validate the selected period based on subscription plan
    const selectedDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    const now = new Date();

    // Check for future dates (no plan allows future dates)
    if (selectedDate > now) {
      setError("Não é possível adicionar períodos futuros.");
      return;
    }

    // Verificar restrições específicas do plano
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(now.getMonth() - 3);

    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(now.getFullYear() - 2);

    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(now.getMonth() - 12);

    // Forçar para o primeiro dia do mês para comparação correta
    threeMonthsAgo.setDate(1);
    threeMonthsAgo.setHours(0, 0, 0, 0);

    twoYearsAgo.setDate(1);
    twoYearsAgo.setHours(0, 0, 0, 0);

    twelveMonthsAgo.setDate(1);
    twelveMonthsAgo.setHours(0, 0, 0, 0);

    let isDateAllowed = false;
    let errorMessage = "";

    if (hasPro) {
      // Business Pro: up to 2 years back
      isDateAllowed = selectedDate >= twoYearsAgo;
      errorMessage = "Seu plano Business Pro permite adicionar períodos de até 2 anos atrás.";
    } else if (hasBusiness && activeAccountType === 'business') {
      // Business: up to 3 months back
      isDateAllowed = selectedDate >= threeMonthsAgo;
      errorMessage = "Seu plano Business permite adicionar períodos de até 3 meses atrás.";
    } else if (userPlanType === 'personal' || activeAccountType === 'personal') {
      // Personal: up to 12 months back
      isDateAllowed = selectedDate >= twelveMonthsAgo; 
      errorMessage = "Seu plano Personal permite adicionar os últimos 12 meses.";
    } else {
      // Default: current month only
      const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      firstDayOfCurrentMonth.setHours(0,0,0,0);
      isDateAllowed = selectedDate.getTime() === firstDayOfCurrentMonth.getTime();
      errorMessage = "Seu plano atual permite adicionar apenas o mês atual.";
    }

    if (!isDateAllowed) {
      setError(errorMessage);
      return;
    }

    setError("");
    setUploading(true);
    setUploadProgress(10);

    try {
      // Verificar usuário atual
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Usuário não autenticado.");

      // Formatar o período (mês/ano)
      const period = `${year}-${month}`;
      const periodLabel = `${months.find(m => m.value === month)?.label} de ${year}`;

      // Analisar o arquivo OFX para extrair informações do banco
      let bankInfo;
      let bankOrg = "Banco Desconhecido";

      try {
        const parseResult = await parseOFXFile(file);
        bankInfo = parseResult.bankInfo;
        bankOrg = bankInfo?.org || "Banco Desconhecido";
      } catch (error) {
        console.error("Erro ao analisar arquivo OFX:", error);
        // Continuar com um valor padrão para a organização bancária
      }

      setUploadProgress(20);

      // Obter o caminho correto para salvar o arquivo
      const { userId, subpath } = await getAccountPath();
      
      // Verificar período existente usando o subcaminho correto
      const existingPeriodQuery = query(
        collection(db, "ofxFiles", userId, subpath),
        where("period", "==", period)
      );

      const existingPeriodSnapshot = await getDocs(existingPeriodQuery);

      // Verificar se algum dos arquivos existentes tem a mesma organização bancária
      let duplicateFound = false;
      let hasSamePeriod = false;

      existingPeriodSnapshot.forEach(doc => {
        const data = doc.data();
        hasSamePeriod = true; // Indica que já existe algum arquivo para este período

        if (data.bankInfo && data.bankInfo.org === bankOrg) {
          duplicateFound = true; // Encontrou um arquivo do mesmo banco
        }
      });

      // MODIFICAÇÃO: Permitir duplicados para business_pro
      // Se um duplicado for encontrado E o usuário NÃO tiver o plano "business_pro"
      if (duplicateFound && !hasPro) {
        setError(`Um arquivo do banco ${bankOrg} já existe para o período ${periodLabel}. Seu plano atual não permite múltiplos arquivos do mesmo banco no mesmo mês. Para substituir, exclua o arquivo existente primeiro ou considere um upgrade de plano.`);
        setUploading(false);
        return;
      }

      setUploadProgress(30);

      // Fazer upload do arquivo para o Firebase Storage
      // Incluir o tipo de conta e o nome do banco no caminho para melhor organização
      const storageFilePath = `ofx/${userId}/${subpath}/${period}/${bankOrg}/${file.name}`;
      const storageRef = ref(storage, storageFilePath);

      await uploadBytes(storageRef, file);
      setUploadProgress(60);

      // Obter URL de download para referência
      const downloadURL = await getDownloadURL(storageRef);

      setUploadProgress(80);

      // Salvar na subcoleção correta do proprietário (ou do próprio usuário se não for colaborador)
      await addDoc(collection(db, "ofxFiles", userId, subpath), {
        fileName: file.name,
        uploadDate: serverTimestamp(),
        month,
        year,
        period,
        periodLabel,
        filePath: storageFilePath,
        fileURL: downloadURL,
        bankInfo: bankInfo || { org: bankOrg },
        bankOrg: bankOrg,
        accountType: activeAccountType,  // Adicionar tipo de conta como metadado
        profileId: activeAccountType === 'business' ? activeBusinessProfileId : null, // Adicionar ID do perfil quando for empresarial
        uploadedBy: isCollaborator ? auth.currentUser.uid : null, // NOVO: Rastrear quem fez o upload se for colaborador
        isCollaboratorUpload: isCollaborator // NOVO: Marcar se foi upload de colaborador
      });

      setUploadProgress(100);

      // Limpar formulário
      setFile(null);
      setMonth("");
      if(document.getElementById('ofx-file-input')) { // Check if element exists before resetting
        document.getElementById('ofx-file-input').value = '';
      }

      // Recarregar períodos
      await loadPeriods();

      // Mensagem de sucesso diferente se adicionou ao período existente
      const successMessage = (hasSamePeriod && !duplicateFound) || (hasSamePeriod && duplicateFound && hasPro)
        ? `Arquivo do banco ${bankOrg} adicionado ao período ${periodLabel}!`
        : `Período ${periodLabel} adicionado com sucesso!`;

      setSuccess(successMessage);
      setTimeout(() => setSuccess(""), 5000);

    } catch (error) {
      console.error("Erro ao adicionar período:", error);
      setError(`Erro ao adicionar período: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Estados para o modal de confirmação
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState(null);

  // Iniciar o processo de exclusão abrindo o modal
  const confirmDeletePeriod = (periodId, periodLabel, filePath, ownerUid) => {
    setPeriodToDelete({
      id: periodId,
      label: periodLabel,
      filePath: filePath,
      ownerUid // NOVO: Incluir ID do proprietário
    });
    setShowConfirmModal(true);
  };

  // Cancelar a exclusão
  const cancelDelete = () => {
    setShowConfirmModal(false);
    setPeriodToDelete(null);
  };

  // Excluir período após confirmação - MODIFICADO para suportar colaboradores
  const handleDeletePeriod = async () => {
    if (!periodToDelete) return;

    // NOVO: Verificar permissões para colaboradores
    if (isCollaborator && !canManagePeriods) {
      setError("Você não tem permissão para excluir períodos neste workspace.");
      setShowConfirmModal(false);
      setPeriodToDelete(null);
      return;
    }

    setLoading(true);
    setShowConfirmModal(false);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Usuário não autenticado.");

      // Obter o ID do proprietário dos dados
      const userId = periodToDelete.ownerUid || currentUser.uid;
      
      // Obter o subcaminho correto
      const { subpath } = await getAccountPath();
      
      // Excluir documento do Firestore usando o subcaminho correto
      await deleteDoc(doc(db, "ofxFiles", userId, subpath, periodToDelete.id));

      // Excluir arquivo do Storage se houver caminho
      if (periodToDelete.filePath) {
        const storageRef = ref(storage, periodToDelete.filePath);
        await deleteObject(storageRef);
      }

      // Atualizar lista de períodos
      setPeriods(prevPeriods => prevPeriods.filter(p => p.id !== periodToDelete.id));

      setSuccess(`Período ${periodToDelete.label} excluído com sucesso!`);
      setTimeout(() => setSuccess(""), 5000);

    } catch (error) {
      console.error("Erro ao excluir período:", error);
      setError(`Erro ao excluir período: ${error.message}`);
    } finally {
      setLoading(false);
      setPeriodToDelete(null);
    }
  };

  // Formatar data
  const formatDate = (date) => {
    if (!date) return "Data inválida";
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Iniciar o download do arquivo OFX
  const handleDownloadFile = async (period) => {
    try {
      // Verificar se temos a informação do caminho do arquivo
      if (!period.filePath) {
        setError("Informação do arquivo não disponível para download.");
        return;
      }

      // Obter a URL de download atualizada
      const storageRef = ref(storage, period.filePath);
      const downloadURL = await getDownloadURL(storageRef);

      // Criar um link temporário para download
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = period.fileName || 'arquivo.ofx';

      // Adicionar o link ao documento, clicar e remover
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erro ao baixar arquivo:", error);
      setError(`Erro ao baixar arquivo: ${error.message}`);
    }
  };

  // MODIFICADO: Obter nome do perfil empresarial ativo com suporte a colaboradores
  const getActiveProfileName = () => {
    if (activeAccountType === 'personal') {
      return 'Conta Pessoal';
    } else if (activeBusinessProfileId) {
      // Buscar o nome do perfil na lista de perfis
      const activeProfile = businessProfiles.find(profile => profile.id === activeBusinessProfileId);
      return activeProfile?.profileName || `Perfil Empresarial (${activeBusinessProfileId})`;
    } else {
      return 'Conta Empresarial';
    }
  };

  return (
    <MainLayout userName={user?.displayName || "Usuário"}>
      <div className="period-manager-container">
        <div className="period-manager-header">
          <h1>
            Gerenciador de Períodos 
            {activeAccountType === 'personal' 
              ? ' (Conta Pessoal)' 
              : ` (${getActiveProfileName()})`}
            {/* NOVO: Indicador de colaborador */}
            {isCollaborator && workspaceOwnerInfo && (
              <span className="collaborator-indicator"> - Workspace de {workspaceOwnerInfo.name}</span>
            )}
          </h1>
          <div className="plan-info">
            {hasPro ? (
              <span className="plan-badge pro">Plano Business Pro</span>
            ) : hasBusiness && activeAccountType === 'business' ? (
              <span className="plan-badge business">Plano Business</span>
            ) : userPlanType === 'personal' || activeAccountType === 'personal' ? (
              <span className="plan-badge basic">Plano Personal</span>
            ) : (
              <span className="plan-badge basic">Plano Básico</span>
            )}
          </div>
        </div>

        {/* NOVO: Aviso para colaboradores sem permissão */}
        {isCollaborator && !canManagePeriods && (
          <div className="collaborator-warning">
            <p>Você está visualizando este workspace como colaborador. Você pode visualizar os períodos, mas não pode adicionar ou excluir arquivos.</p>
          </div>
        )}

        {/* Informação sobre restrições do plano com detalhes extras */}
        <div className={`plan-restrictions ${hasPro ? 'pro' : (hasBusiness && activeAccountType === 'business' ? 'business' : 'basic')}`}>
          {hasPro ? (
            <p>Seu plano <strong>Business Pro</strong> permite adicionar períodos de até <strong>2 anos atrás</strong>. Você pode adicionar múltiplos arquivos do mesmo banco para o mesmo mês.</p>
          ) : hasBusiness && activeAccountType === 'business' ? (
            <p>Seu plano <strong>Business</strong> permite adicionar períodos de até <strong>3 meses atrás</strong>.</p>
          ) : userPlanType === 'personal' || activeAccountType === 'personal' ? (
            <p>Sua <strong>Conta Pessoal</strong> permite adicionar os <strong>últimos 12 meses</strong>.</p>
          ) : (
            <p>Seu plano atual permite adicionar apenas o <strong>mês corrente</strong>.</p>
          )}
          <p className="plan-details">
            Tipo de conta ativa: <strong>{activeAccountType === 'personal' ? 'Pessoal' : 'Empresarial'}</strong>
            {activeAccountType === 'business' && activeBusinessProfileId && (
              <> | Perfil ativo: <strong>{getActiveProfileName()}</strong></>
            )}
          </p>
        </div>

        {error && (
          <div className="error-message">
            <button className="close-button" onClick={() => setError("")}>×</button>
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            <button className="close-button" onClick={() => setSuccess("")}>×</button>
            {success}
          </div>
        )}

        <div className="period-manager-content">
          {/* Formulário para adicionar novo período - ocultar para colaboradores sem permissão */}
          {(!isCollaborator || canManagePeriods) && (
            <div className="add-period-card">
              <h2>Adicionar Novo Período</h2>

              <div className="add-period-form">
                <div className="form-group">
                  <label htmlFor="ofx-file-input">Arquivo OFX</label>
                  <div className="file-input-container">
                    <input
                      type="file"
                      id="ofx-file-input"
                      accept=".ofx,.OFX"
                      onChange={handleFileChange}
                      disabled={uploading}
                      className="file-input"
                    />
                    <label htmlFor="ofx-file-input" className="file-label">
                      {file ? file.name : "Escolher arquivo OFX"}
                    </label>
                  </div>
                </div>

                <div className="period-selection">
                  <div className="form-group">
                    <label htmlFor="year-select">Ano</label>
                    <select
                      id="year-select"
                      value={year}
                      onChange={(e) => setYear(parseInt(e.target.value))}
                      disabled={uploading}
                      className="period-select"
                    >
                      {availableYears.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="month-select">Mês</label>
                    <select
                      id="month-select"
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      disabled={uploading || !availableMonths.length} // Disable if no months available
                      className="period-select"
                    >
                      <option value="">Selecione o mês</option>
                      {availableMonths.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  className="add-period-button"
                  onClick={handleAddPeriod}
                  disabled={!file || !month || uploading}
                >
                  {uploading ? "Adicionando..." : "Adicionar Período"}
                </button>

                {uploading && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <div className="progress-text">{uploadProgress}% Concluído</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lista de períodos existentes */}
          <div className="periods-list-card">
            <h2>
              Períodos Existentes 
              {activeAccountType === 'personal' 
                ? ' (Conta Pessoal)' 
                : ` (${getActiveProfileName()})`}
              {/* NOVO: Indicador de colaborador */}
              {isCollaborator && workspaceOwnerInfo && (
                <span className="collaborator-indicator"> - Workspace de {workspaceOwnerInfo.name}</span>
              )}
            </h2>

            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Carregando períodos...</p>
              </div>
            ) : periods.length === 0 ? (
              <div className="no-periods">
                <p>Nenhum período encontrado. {(!isCollaborator || canManagePeriods) ? 'Adicione um novo período utilizando o formulário acima.' : 'Entre em contato com o proprietário do workspace para adicionar períodos.'}</p>
              </div>
            ) : (
              <div className="periods-list">
                <table className="periods-table">
                  <thead>
                    <tr>
                      <th>Período</th>
                      <th>Banco</th>
                      <th>Arquivo</th>
                      <th>Data de Upload</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map((period) => (
                      <tr key={period.id}>
                        <td>{period.periodLabel}</td>
                        <td>{period.bankInfo?.org || "Desconhecido"}</td>
                        <td className="file-name-cell">{period.fileName}</td>
                        <td>{formatDate(period.uploadDate)}</td>
                        <td className="action-buttons">
                          <button
                            className="download-button"
                            onClick={() => handleDownloadFile(period)}
                            disabled={loading}
                          >
                            Baixar
                          </button>
                          {/* Mostrar botão de exclusão apenas para usuários com permissão */}
                          {(!isCollaborator || canManagePeriods) && (
                            <button
                              className="delete-button"
                              onClick={() => confirmDeletePeriod(period.id, period.periodLabel, period.filePath, period.ownerUid)}
                              disabled={loading}
                            >
                              Excluir
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmação de exclusão */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-modal-header">
              <h3>Confirmar Exclusão</h3>
            </div>
            <div className="confirm-modal-content">
              <p>Tem certeza que deseja excluir o arquivo referente ao período <strong>{periodToDelete?.label}</strong>?</p>
              <p className="warning-text">Esta ação não pode ser desfeita.</p>
            </div>
            <div className="confirm-modal-footer">
              <button
                className="cancel-button"
                onClick={cancelDelete}
              >
                Cancelar
              </button>
              <button
                className="confirm-button"
                onClick={handleDeletePeriod}
              >
                Confirmar Exclusão
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default PeriodManager;